import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Plus, Trash2, Edit2, Save, X, MapPin, Phone, Mail, Briefcase, Upload, Image, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useBusiness } from '../context/BusinessContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const businessTypes = [
  'Retail Store',
  'Restaurant',
  'Cafe',
  'Salon & Spa',
  'Gym & Fitness',
  'Hotel',
  'Office',
  'Shopping Mall',
  'Supermarket',
  'Other'
];

// Generate hours for time picker (0-24)
const generateHours = () => {
  const hours = [];
  for (let i = 0; i <= 24; i++) {
    const label = i === 0 ? '12 AM' : i === 12 ? '12 PM' : i === 24 ? '12 AM (next day)' : i < 12 ? `${i} AM` : `${i - 12} PM`;
    hours.push({ value: i, label });
  }
  return hours;
};

const HOURS = generateHours();

const BusinessManagement = () => {
  const { refreshBusinesses } = useBusiness();
  const { isLight } = useTheme();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState(null);
  const [uploadingLogoFor, setUploadingLogoFor] = useState(null);
  const logoInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact_phone: '',
    contact_email: '',
    field_of_business: '',
    opening_time: 9,  // Default 9 AM
    closing_time: 22  // Default 10 PM
  });

  useEffect(() => {
    fetchBusinesses();
  }, []);

  const fetchBusinesses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusinesses(response.data);
    } catch (error) {
      toast.error('Failed to fetch businesses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Business name is required');
      return;
    }
    
    // Validate store hours
    if (formData.opening_time >= formData.closing_time) {
      toast.error('Opening time must be before closing time');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/businesses`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business created successfully');
      setCreateOpen(false);
      setFormData({ name: '', address: '', contact_phone: '', contact_email: '', field_of_business: '', opening_time: 9, closing_time: 22 });
      fetchBusinesses();
      refreshBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create business');
    }
  };

  const handleUpdate = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/businesses/${businessId}`, editingBusiness, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business updated successfully');
      setEditingBusiness(null);
      fetchBusinesses();
      refreshBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update business');
    }
  };

  const handleDelete = async (businessId, businessName) => {
    if (!window.confirm(`Are you sure you want to delete "${businessName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business deleted successfully');
      fetchBusinesses();
      refreshBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete business');
    }
  };

  const handleLogoUpload = async (e, businessId) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (PNG, JPG, WEBP, or SVG)');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file must be less than 2MB');
      return;
    }

    setUploadingLogoFor(businessId);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/businesses/${businessId}/logo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Logo uploaded successfully');
      fetchBusinesses();
      refreshBusinesses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogoFor(null);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getLogoUrl = (business) => {
    if (!business.logo_url) return null;
    return business.logo_url.startsWith('http') 
      ? business.logo_url 
      : `${BACKEND_URL}${business.logo_url}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={isLight ? 'text-gray-500' : 'text-slate-400'}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={`p-8 min-h-screen ${isLight ? 'bg-[#faf8f5]' : ''}`}>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-3xl font-bold mb-2 flex items-center gap-3 ${isLight ? 'text-gray-800' : 'text-white'}`}>
              <Building2 className={`w-8 h-8 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
              Business Management
            </h1>
            <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>Create and manage business accounts</p>
          </div>
          
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className={`rounded-full px-6 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'} text-white`}>
                <Plus className="w-4 h-4 mr-2" />
                Add Business
              </Button>
            </DialogTrigger>
            <DialogContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
              <DialogHeader>
                <DialogTitle className={isLight ? 'text-gray-800' : ''}>Create New Business</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Business Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Sunrise Cafe"
                    className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    required
                  />
                </div>
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street, City"
                    className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={isLight ? 'text-gray-600' : ''}>Contact Phone</Label>
                    <Input
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      placeholder="+1 234 567 8900"
                      className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    />
                  </div>
                  <div>
                    <Label className={isLight ? 'text-gray-600' : ''}>Contact Email</Label>
                    <Input
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      placeholder="contact@business.com"
                      className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className={isLight ? 'text-gray-600' : ''}>Field of Business</Label>
                  <Select
                    value={formData.field_of_business}
                    onValueChange={(value) => setFormData({ ...formData, field_of_business: value })}
                  >
                    <SelectTrigger className={`mt-1.5 ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-800 text-white'}`}>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                      {businessTypes.map((type) => (
                        <SelectItem key={type} value={type} className={isLight ? 'hover:bg-gray-100' : ''}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Store Hours - Required */}
                <div className={`border rounded-lg p-4 ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-800 bg-slate-950/50'}`}>
                  <Label className={`flex items-center gap-2 mb-3 font-medium ${isLight ? 'text-gray-700' : 'text-white'}`}>
                    <Clock className={`w-4 h-4 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                    Store Hours *
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Opening Time</Label>
                      <Select
                        value={formData.opening_time.toString()}
                        onValueChange={(value) => setFormData({ ...formData, opening_time: parseInt(value) })}
                      >
                        <SelectTrigger className={`mt-1.5 ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                          {HOURS.filter(h => h.value < 24).map((hour) => (
                            <SelectItem key={hour.value} value={hour.value.toString()} className={isLight ? 'hover:bg-gray-100' : ''}>
                              {hour.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className={`text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>Closing Time</Label>
                      <Select
                        value={formData.closing_time.toString()}
                        onValueChange={(value) => setFormData({ ...formData, closing_time: parseInt(value) })}
                      >
                        <SelectTrigger className={`mt-1.5 ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-800 text-white'}>
                          {HOURS.filter(h => h.value > formData.opening_time).map((hour) => (
                            <SelectItem key={hour.value} value={hour.value.toString()} className={isLight ? 'hover:bg-gray-100' : ''}>
                              {hour.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className={`text-xs mt-2 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                    Weekly schedule will display time slots based on these hours
                  </p>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCreateOpen(false)}
                    className={`flex-1 ${isLight ? 'border-[#ebe7e0] text-gray-600 hover:bg-gray-50' : 'border-slate-700'}`}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" className={`flex-1 ${isLight ? 'bg-[#e94560] hover:bg-[#d63d56]' : 'bg-rose-600 hover:bg-rose-500'}`}>
                    Create Business
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className={`text-2xl font-bold ${isLight ? 'text-gray-800' : 'text-white'}`}>{businesses.length}</div>
            <div className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Total Businesses</div>
          </div>
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className={`text-2xl font-bold ${isLight ? 'text-[#8b9a7d]' : 'text-emerald-400'}`}>
              {businesses.filter(b => b.logo_url).length}
            </div>
            <div className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>With Logo</div>
          </div>
          <div className={`rounded-xl border p-4 ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
            <div className={`text-2xl font-bold ${isLight ? 'text-[#c4a35a]' : 'text-amber-400'}`}>
              {businesses.filter(b => !b.logo_url).length}
            </div>
            <div className={`text-sm ${isLight ? 'text-gray-400' : 'text-slate-400'}`}>Without Logo</div>
          </div>
        </div>

        {/* Business List */}
        <div className={`rounded-xl border overflow-hidden ${isLight ? 'border-[#ebe7e0] bg-white' : 'border-slate-800 bg-slate-900/40'}`}>
          <div className={`p-4 border-b ${isLight ? 'border-[#ebe7e0]' : 'border-slate-800'}`}>
            <h2 className={`text-lg font-semibold ${isLight ? 'text-gray-800' : 'text-white'}`}>All Businesses</h2>
          </div>
          
          {businesses.length === 0 ? (
            <div className="p-8 text-center">
              <Building2 className={`w-12 h-12 mx-auto mb-4 ${isLight ? 'text-gray-300' : 'text-slate-600'}`} />
              <p className={isLight ? 'text-gray-500' : 'text-slate-400'}>No businesses yet. Create your first business to get started.</p>
            </div>
          ) : (
            <div className={`divide-y ${isLight ? 'divide-[#ebe7e0]' : 'divide-slate-800'}`}>
              {businesses.map((business) => (
                <div key={business.id} className={`p-4 transition-colors ${isLight ? 'hover:bg-gray-50' : 'hover:bg-slate-800/30'}`}>
                  {editingBusiness?.id === business.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          value={editingBusiness.name}
                          onChange={(e) => setEditingBusiness({ ...editingBusiness, name: e.target.value })}
                          placeholder="Business Name"
                          className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}
                        />
                        <Input
                          value={editingBusiness.address || ''}
                          onChange={(e) => setEditingBusiness({ ...editingBusiness, address: e.target.value })}
                          placeholder="Address"
                          className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}
                        />
                        <Input
                          value={editingBusiness.contact_phone || ''}
                          onChange={(e) => setEditingBusiness({ ...editingBusiness, contact_phone: e.target.value })}
                          placeholder="Phone"
                          className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}
                        />
                        <Input
                          value={editingBusiness.contact_email || ''}
                          onChange={(e) => setEditingBusiness({ ...editingBusiness, contact_email: e.target.value })}
                          placeholder="Email"
                          className={isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-800' : 'bg-slate-950 border-slate-700 text-white'}
                        />
                      </div>
                      {/* Store Hours Edit */}
                      <div className={`border rounded-lg p-3 ${isLight ? 'border-[#ebe7e0] bg-[#faf8f5]' : 'border-slate-700 bg-slate-900/50'}`}>
                        <Label className={`flex items-center gap-2 mb-2 text-sm font-medium ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
                          <Clock className={`w-3.5 h-3.5 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                          Store Hours
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                          <Select
                            value={(editingBusiness.opening_time || 8).toString()}
                            onValueChange={(value) => setEditingBusiness({ ...editingBusiness, opening_time: parseInt(value) })}
                          >
                            <SelectTrigger className={`h-9 ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-800 border-slate-600 text-white'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}>
                              {HOURS.filter(h => h.value < 24).map((hour) => (
                                <SelectItem key={hour.value} value={hour.value.toString()} className={isLight ? 'hover:bg-gray-100' : ''}>
                                  {hour.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={(editingBusiness.closing_time || 23).toString()}
                            onValueChange={(value) => setEditingBusiness({ ...editingBusiness, closing_time: parseInt(value) })}
                          >
                            <SelectTrigger className={`h-9 ${isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-800 border-slate-600 text-white'}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className={isLight ? 'bg-white border-[#ebe7e0] text-gray-800' : 'bg-slate-900 border-slate-700 text-white'}>
                              {HOURS.filter(h => h.value > (editingBusiness.opening_time || 8)).map((hour) => (
                                <SelectItem key={hour.value} value={hour.value.toString()} className={isLight ? 'hover:bg-gray-100' : ''}>
                                  {hour.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className={`text-xs mt-1.5 ${isLight ? 'text-amber-600' : 'text-amber-400'}`}>
                          ⚠️ Changing hours will auto-remove schedules outside new hours
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleUpdate(business.id)}
                          className="bg-emerald-600 hover:bg-emerald-500"
                        >
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </Button>
                        <Button
                          onClick={() => setEditingBusiness(null)}
                          variant="outline"
                          className={isLight ? 'border-[#ebe7e0]' : 'border-slate-700'}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        {/* Logo / Placeholder */}
                        <div className="relative group">
                          {getLogoUrl(business) ? (
                            <img 
                              src={getLogoUrl(business)}
                              alt={business.name}
                              className={`w-16 h-16 rounded-xl object-contain p-1 ${isLight ? 'bg-gray-100' : 'bg-white/10'}`}
                            />
                          ) : (
                            <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${isLight ? 'bg-[#e94560]/10' : 'bg-rose-600/20'}`}>
                              <Building2 className={`w-8 h-8 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`} />
                            </div>
                          )}
                          {/* Upload overlay on hover */}
                          <label 
                            className="absolute inset-0 bg-black/60 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                          >
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                              onChange={(e) => handleLogoUpload(e, business.id)}
                              className="hidden"
                              ref={uploadingLogoFor === business.id ? logoInputRef : null}
                            />
                            {uploadingLogoFor === business.id ? (
                              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                            ) : (
                              <div className="text-center">
                                <Upload className="w-5 h-5 text-white mx-auto" />
                                <span className="text-[10px] text-white">Upload</span>
                              </div>
                            )}
                          </label>
                        </div>
                        
                        <div>
                          <h3 className={`font-semibold text-lg ${isLight ? 'text-gray-800' : 'text-white'}`}>{business.name}</h3>
                          <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-sm ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
                            {business.field_of_business && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="w-3.5 h-3.5" />
                                {business.field_of_business}
                              </span>
                            )}
                            {business.address && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {business.address}
                              </span>
                            )}
                            {business.contact_phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3.5 h-3.5" />
                                {business.contact_phone}
                              </span>
                            )}
                            {business.contact_email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3.5 h-3.5" />
                                {business.contact_email}
                              </span>
                            )}
                            {/* Store Hours Display */}
                            <span className={`flex items-center gap-1 ${isLight ? 'text-[#e94560]' : 'text-rose-400'}`}>
                              <Clock className="w-3.5 h-3.5" />
                              {HOURS.find(h => h.value === (business.opening_time || 8))?.label || '8 AM'} - {HOURS.find(h => h.value === (business.closing_time || 23))?.label || '11 PM'}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-2">
                            <span className={`text-xs ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
                              Created: {formatDate(business.created_at)}
                            </span>
                            {!business.logo_url && (
                              <span className={`text-xs flex items-center gap-1 ${isLight ? 'text-[#c4a35a]' : 'text-amber-500'}`}>
                                <Image className="w-3 h-3" />
                                No logo - hover to upload
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setEditingBusiness(business)}
                          variant="ghost"
                          className={`h-9 w-9 p-0 ${isLight ? 'hover:bg-gray-100' : 'hover:bg-slate-700'}`}
                        >
                          <Edit2 className={`w-4 h-4 ${isLight ? 'text-gray-400' : 'text-slate-400'}`} />
                        </Button>
                        <Button
                          onClick={() => handleDelete(business.id, business.name)}
                          variant="ghost"
                          className={`h-9 w-9 p-0 ${isLight ? 'hover:bg-red-50' : 'hover:bg-red-900/30'}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className={`border rounded-lg p-4 text-sm ${isLight ? 'bg-[#faf8f5] border-[#ebe7e0] text-gray-600' : 'bg-slate-800/30 border-slate-700 text-slate-300'}`}>
          <p className="font-medium mb-2">Logo Upload Tips:</p>
          <ul className={`list-disc list-inside space-y-1 ${isLight ? 'text-gray-500' : 'text-slate-400'}`}>
            <li>Hover over any business logo/icon to upload a new logo</li>
            <li>Supported formats: PNG, JPG, WEBP, SVG</li>
            <li>Maximum file size: 2MB</li>
            <li>Logos will be displayed on the Dashboard for users of that business</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BusinessManagement;
